import type { HeaderMenuConfiguration } from "../interfaces";

// Constants
import actions, { Action } from "@/Constants/actionsList";
import positions from "@/Constants/positions";

// Helpers
import { getPositionIcon } from "@Common/helpers/Positions/icons";

// Icons
import Comparison from "@/assets/icons/header/payplans/Comparison.svg";
import Group from "@/assets/icons/header/payplans/Group.svg";
import Individual from "@/assets/icons/header/payplans/Individual.svg";
import Projection from "@/assets/icons/header/payplans/Projection.svg";
import Historical from "@/assets/icons/header/payplans/Historical.svg";
import Current from "@/assets/icons/header/payplans/Current.svg";
import ComparisonDark from "@/assets/icons/header/payplans/Comparison Dark.svg";
import GroupDark from "@/assets/icons/header/payplans/Group Dark.svg";
import IndividualDark from "@/assets/icons/header/payplans/Individual Dark.svg";
import ProjectionDark from "@/assets/icons/header/payplans/Projection Dark.svg";
import HistoricalDark from "@/assets/icons/header/payplans/Historical Dark.svg";
import CurrentDark from "@/assets/icons/header/payplans/Current Dark.svg";

const MATCHES_CUSTOMER_WITH_IDEAL_CAR = "Matches customers with their ideal car";

const getHistoricalAssignmentsActions = () => {
	return [
		actions.VIEW_HISTORICAL_ASSIGNMENTS,
		actions.VIEW_SALES_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_SALES_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_FINANCE_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_FINANCE_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_GENERAL_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_GENERAL_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_FINANCE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_FINANCE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_GENERAL_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_GENERAL_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BDC_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BDC_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BDC_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BDC_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BDC_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BDC_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_SERVICE_ADVISOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_SERVICE_ADVISOR_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_SERVICE_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_SERVICE_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_SERVICE_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_SERVICE_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_SERVICE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_SERVICE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BODY_SHOP_ESTIMATOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BODY_SHOP_ESTIMATOR_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BODY_SHOP_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BODY_SHOP_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BODY_SHOP_PAINTER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BODY_SHOP_PAINTER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BODY_SHOP_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BODY_SHOP_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BODY_SHOP_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BODY_SHOP_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_FIXED_OPERATIONS_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_FIXED_OPERATIONS_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_BODY_SHOP_PRODUCT_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_BODY_SHOP_PRODUCT_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_PARTS_COUNTER_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_PARTS_COUNTER_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_PARTS_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_PARTS_MANAGER_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_PARTS_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
		actions.EDIT_PARTS_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
	];
};

const PayPlansMenuConfiguration: HeaderMenuConfiguration = {
	title: "Pay Plans",
	actions: [
		actions.VIEW_GENERAL_MANAGER_PAY_PLANS,
		actions.VIEW_SALES_ASSOCIATE_PAY_PLANS,
		actions.VIEW_SALES_MANAGER_PAY_PLANS,
		actions.VIEW_GENERAL_SALES_MANAGER_PAY_PLANS,
		actions.VIEW_FINANCE_MANAGER_PAY_PLANS,
		actions.VIEW_FINANCE_DIRECTOR_PAY_PLANS,
		actions.VIEW_BDC_MANAGER_PAY_PLANS,
		actions.VIEW_BDC_ASSOCIATE_PAY_PLANS,
		actions.VIEW_SERVICE_TECHNICIAN_PAY_PLANS,
		actions.VIEW_SERVICE_ADVISOR_PAY_PLANS,
		actions.VIEW_SERVICE_MANAGER_PAY_PLANS,
		actions.VIEW_SERVICE_DIRECTOR_PAY_PLANS,
		actions.VIEW_SERVICE_FOREMAN_PAY_PLANS,
		actions.VIEW_INTERNAL_SERVICE_ADVISOR_PAY_PLANS,
		actions.VIEW_BODY_SHOP_MANAGER_PAY_PLANS,
		actions.VIEW_BODY_SHOP_PRODUCT_MANAGER_PAY_PLANS,
		actions.VIEW_BODY_SHOP_FOREMAN_PAY_PLANS,
		actions.VIEW_BODY_SHOP_ESTIMATOR_PAY_PLANS,
		actions.VIEW_BODY_SHOP_PAINTER_PAY_PLANS,
		actions.VIEW_BODY_SHOP_TECHNICIAN_PAY_PLANS,
		actions.VIEW_PARTS_COUNTER_ASSOCIATE_PAY_PLANS,
		actions.VIEW_PARTS_MANAGER_PAY_PLANS,
		actions.VIEW_PARTS_DIRECTOR_PAY_PLANS,
		actions.VIEW_PAY_PLAN_ASSIGNMENTS,
		actions.VIEW_HISTORICAL_ASSIGNMENTS,
		actions.COMPARE_PAY_PLANS,
		actions.PAY_PLAN_PROJECTION,
	],
	menuConfiguration: {
		submenuMinWidth: 556,
		submenuHeight: "calculated",
		sections: [
			{
				name: "Location",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "General Manager",
								description: MATCHES_CUSTOMER_WITH_IDEAL_CAR,
								icon: getPositionIcon({ position: positions.LOCATION.GENERAL_MANAGER }),
								url: "",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_GENERAL_MANAGER_PAY_PLANS,
									position: positions.LOCATION.GENERAL_MANAGER,
								},
							},
						],
					},
				],
			},
			{
				name: "Sales",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "General Sales Manager",
								description: "Oversees sales department",
								icon: getPositionIcon({ position: positions.SALES.GENERAL_SALES_MANAGER }),
								url: "/pay-plans/general-sales-managers",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_GENERAL_SALES_MANAGER_PAY_PLANS,
									position: positions.SALES.GENERAL_SALES_MANAGER,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Sales Manager",
								description: "Sales associate supervisor/motivator",
								icon: getPositionIcon({ position: positions.SALES.SALES_MANAGER }),
								url: "/pay-plans/sales-managers",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SALES_MANAGER_PAY_PLANS,
									position: positions.SALES.SALES_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Sales Associates",
								description: MATCHES_CUSTOMER_WITH_IDEAL_CAR,
								icon: getPositionIcon({ position: positions.SALES.SALES_ASSOCIATE }),
								url: "/pay-plans/sales-associates",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SALES_ASSOCIATE_PAY_PLANS,
									position: positions.SALES.SALES_ASSOCIATE,
								},
							},
						],
					},
				],
			},
			{
				name: "F&I",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Finance Director",
								description: "Leads the finance department",
								icon: getPositionIcon({ position: positions.FINANCE.FINANCE_DIRECTOR }),
								url: "/pay-plans/finance-directors",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_FINANCE_DIRECTOR_PAY_PLANS,
									position: positions.FINANCE.FINANCE_DIRECTOR,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "F&I Manager",
								description: "Structures vehicle deals for customers",
								icon: getPositionIcon({ position: positions.FINANCE.FINANCE_MANAGER }),
								url: "/pay-plans/finance-managers",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_FINANCE_MANAGER_PAY_PLANS,
									position: positions.FINANCE.FINANCE_MANAGER,
								},
							},
						],
					},
				],
			},
			{
				name: "BDC",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "BDC Manager",
								description: "Leads the BDC department",
								icon: getPositionIcon({ position: positions.BDC.BDC_MANAGER }),
								url: "",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BDC_MANAGER_PAY_PLANS,
									position: positions.BDC.BDC_MANAGER,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "BDC Service Associate",
								description: "Sets up all service interactions",
								icon: getPositionIcon({ position: positions.BDC.BDC_SERVICE_ASSOCIATE }),
								url: "",
								accessConfiguration: false,
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "BDC Sales Associate",
								description: "Handles sales leads and inquiries",
								icon: getPositionIcon({ position: positions.BDC.BDC_SALES_ASSOCIATE }),
								url: "",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BDC_ASSOCIATE_PAY_PLANS,
									position: positions.BDC.BDC_SALES_ASSOCIATE,
								},
							},
						],
					},
				],
			},
			{
				name: "Service",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Service Directors",
								description: "Directs all service related activities",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_DIRECTOR }),
								url: "/pay-plans/service-directors",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_DIRECTOR_PAY_PLANS,
									position: positions.SERVICE.SERVICE_DIRECTOR,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Service Manager",
								description: "Supervises all service processes",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_MANAGER }),
								url: "/pay-plans/service-managers",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_MANAGER_PAY_PLANS,
									position: positions.SERVICE.SERVICE_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Service Foreman",
								description: "Foresees all service processes",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_FOREMAN }),
								url: "/pay-plans/service-foreman",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_FOREMAN_PAY_PLANS,
									position: positions.SERVICE.SERVICE_FOREMAN,
								},
							},
						],
					},
					{
						id: 3,
						menuItems: [
							{
								label: "Service Advisors",
								description: "Handles customers vehicle needs",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_ADVISOR }),
								url: "/pay-plans/service-advisors",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_ADVISOR_PAY_PLANS,
									position: positions.SERVICE.SERVICE_ADVISOR,
								},
							},
						],
					},
					{
						id: 4,
						menuItems: [
							{
								label: "Internal Service Advisors",
								description: "Handles customer vehicle needs",
								icon: getPositionIcon({ position: positions.SERVICE.INTERNAL_SERVICE_ADVISOR }),
								url: "/pay-plans/internal-service-advisors",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_INTERNAL_SERVICE_ADVISOR_PAY_PLANS,
									position: positions.SERVICE.INTERNAL_SERVICE_ADVISOR,
								},
							},
						],
					},
					{
						id: 5,
						menuItems: [
							{
								label: "Service Technician",
								description: "Repair and maintenance of vehicles",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_TECHNICIAN }),
								url: "/pay-plans/service-technicians",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_TECHNICIAN_PAY_PLANS,
									position: positions.SERVICE.SERVICE_TECHNICIAN,
								},
							},
						],
					},
				],
			},
			{
				name: "Body Shop",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Body Shop Manager",
								description: "Supervises the body shop department",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_MANAGER }),
								url: "/pay-plans/body-shop-managers",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_MANAGER_PAY_PLANS,
									position: positions.BODY_SHOP.BODY_SHOP_MANAGER,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Body Shop Prod. Manager",
								description: "Maintains industry quality standards",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_PRODUCT_MANAGER }),
								url: "/pay-plans/body-shop-product-managers",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_PRODUCT_MANAGER_PAY_PLANS,
									position: positions.BODY_SHOP.BODY_SHOP_PRODUCT_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Body Shop Foreman",
								description: "Foresees the body shop department",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_FOREMAN }),
								url: "/pay-plans/body-shop-foreman",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_FOREMAN_PAY_PLANS,
									position: positions.BODY_SHOP.BODY_SHOP_FOREMAN,
								},
							},
						],
					},
					{
						id: 3,
						menuItems: [
							{
								label: "Body Shop Estimator",
								description: "Estimates standard and custom paint jobs",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_ESTIMATOR }),
								url: "/pay-plans/body-shop-estimators",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_ESTIMATOR_PAY_PLANS,
									position: positions.BODY_SHOP.BODY_SHOP_ESTIMATOR,
								},
							},
						],
					},
					{
						id: 4,
						menuItems: [
							{
								label: "Body Shop Painter",
								description: "Arranges standard and custom paint jobs",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_PAINTER }),
								url: "/pay-plans/body-shop-painters",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_PAINTER_PAY_PLANS,
									position: positions.BODY_SHOP.BODY_SHOP_PAINTER,
								},
							},
						],
					},
					{
						id: 5,
						menuItems: [
							{
								label: "Body Shop Technician",
								description: "Repairs body parts and bodies of vehicles",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_TECHNICIAN }),
								url: "/pay-plans/body-shop-technicians",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_TECHNICIAN_PAY_PLANS,
									position: positions.BODY_SHOP.BODY_SHOP_TECHNICIAN,
								},
							},
						],
					},
				],
			},
			{
				name: "Parts",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Parts Directors",
								description: "Chief of all things parts",
								icon: getPositionIcon({ position: positions.PARTS.PARTS_DIRECTOR }),
								url: "/pay-plans/parts-directors",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_PARTS_DIRECTOR_PAY_PLANS,
									position: positions.PARTS.PARTS_DIRECTOR,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Parts Manager",
								description: "Oversees the parts department",
								icon: getPositionIcon({ position: positions.PARTS.PARTS_MANAGER }),
								url: "/pay-plans/parts-managers",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_PARTS_MANAGER_PAY_PLANS,
									position: positions.PARTS.PARTS_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Parts Counter Associate",
								description: "Parts sales & customer service",
								icon: getPositionIcon({ position: positions.PARTS.PARTS_COUNTER_ASSOCIATE }),
								url: "/pay-plans/parts-counter-associates",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_PARTS_COUNTER_ASSOCIATE_PAY_PLANS,
									position: positions.PARTS.PARTS_COUNTER_ASSOCIATE,
								},
							},
						],
					},
				],
			},
		],
		footerItems: [
			{
				label: "Individual Assignments",
				icon: Individual,
				iconHover: IndividualDark,
				url: "/pay-plans/individual-assignments",
				accessConfiguration: {
					type: "authorized",
					access: actions.VIEW_PAY_PLAN_ASSIGNMENTS,
				},
			},
			{
				label: "Current Assignments Report",
				icon: Current,
				iconHover: CurrentDark,
				url: "/pay-plans/current-assignments",
				accessConfiguration: {
					type: "authorized",
					access: actions.VIEW_PAY_PLAN_ASSIGNMENTS,
				},
			},
			{
				label: "Group Assignments",
				icon: Group,
				iconHover: GroupDark,
				url: "/pay-plans/group-assignments",
				accessConfiguration: {
					type: "authorized",
					access: actions.VIEW_PAY_PLAN_ASSIGNMENTS,
				},
			},
			{
				label: "Historical Assignments",
				icon: Historical,
				iconHover: HistoricalDark,
				url: "/pay-plans/history-assignments",
				accessConfiguration: {
					type: "authorized",
					access: getHistoricalAssignmentsActions() as [Action],
				},
			},
			{
				label: "Performance Projection",
				icon: Projection,
				iconHover: ProjectionDark,
				url: "",
				accessConfiguration: {
					type: "authorized",
					access: actions.PAY_PLAN_PROJECTION,
				},
			},
			{
				label: "Performance Comparison",
				icon: Comparison,
				iconHover: ComparisonDark,
				url: "",
				accessConfiguration: {
					type: "authorized",
					access: actions.COMPARE_PAY_PLANS,
				},
			},
		],
	},
};

export default PayPlansMenuConfiguration;
